/**
 * The keyboard dispatcher (design/ux/01_foundations.md §4.3-§4.4, WP-17).
 *
 * ONE capture-phase `keydown` listener for the pharmacy shell. Components do
 * not add their own window listeners for registry keys: they register a SCOPE
 * (hooks/useShortcuts.js) — its bindings come from config/shortcuts.config.js
 * and its handlers are keyed by the binding ids — and the dispatcher decides,
 * for every key press, which single handler runs:
 *
 *  1. IME composition and AltGr (Ctrl+Alt on AZERTY) never fire (rules 1, 4).
 *  2. Innermost scope wins (rule 5): overlay scopes (last registered first) →
 *     screen scopes (last registered first) → the global scope. Bare keys
 *     pressed inside a focused widget (grid, listbox, menu, combobox, tabs,
 *     tree, `[data-own-keys]`) belong to that widget and skip the screen map.
 *  3. While a MODAL overlay is open the screen maps and the global keys are
 *     suspended: only overlay scopes and F1 fire; Échap stays with the dialog;
 *     the navigation keys are swallowed (a dialog without F9 can never
 *     validate the page behind it).
 *  4. Inside a text field only F-keys, Échap and Ctrl / Alt chords fire, never
 *     Ctrl+C/V/X/Z/Y/A (rule 2).
 *  5. A held key never repeats a commit verb (rule 3).
 *  6. A screen holding unsaved work may declare `suppressGlobal: ["nav"]`:
 *     the navigation keys then show a message instead of navigating (rule 6).
 *  7. A legacy screen (the POS tills and their hand-written listeners, whose
 *     maps stay byte-identical) owns the global keys its own map declares
 *     (F1, Échap): the dispatcher leaves those events untouched.
 *
 * A handler is a function `(event, { index, binding }) => unknown` (returning
 * `false` means « not handled »: the browser default is kept) or an object
 * `{ run, disabled, disabledReason }` — a disabled verb swallows the key and
 * says why.
 *
 * Pure module: the DOM (modal detection, the listener) is injected, so the
 * rules are unit-tested with plain event objects.
 */
import { firesInTextField, isAltGraph, isTextField, matchCombo, parseCombo } from "./key-combo.js";
import { GLOBAL_SHORTCUTS, SCREEN_SHORTCUTS, SUPPRESS_GROUPS } from "../config/shortcuts.config.js";

export const SCOPE_KINDS = Object.freeze({
  GLOBAL: "global",
  SCREEN: "screen",
  LEGACY: "legacy",
  OVERLAY: "overlay",
});

/** Global bindings that still fire while a modal overlay is open. */
const MODAL_GLOBAL_IDS = Object.freeze(["AIDE"]);

/** A focused widget owns its bare keys (arrows, Entrée, Espace, Suppr, + −, digits). */
export const WIDGET_SELECTOR = [
  '[role="grid"]',
  '[role="treegrid"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[role="menubar"]',
  '[role="tree"]',
  '[role="tablist"]',
  '[role="combobox"]',
  "[data-own-keys]",
].join(",");

function isInsideWidget(target) {
  return Boolean(target && typeof target.closest === "function" && target.closest(WIDGET_SELECTOR));
}

const parsedCache = new Map();
function parsed(spec) {
  let c = parsedCache.get(spec);
  if (!c) {
    c = parseCombo(spec);
    parsedCache.set(spec, c);
  }
  return c;
}

function readHandler(h) {
  if (typeof h === "function") return { run: h, disabled: false };
  if (h && typeof h === "object" && typeof h.run === "function") {
    return { run: h.run, disabled: Boolean(h.disabled), disabledReason: h.disabledReason };
  }
  return null;
}

/**
 * @typedef {Object} ScopeSpec
 * @property {string}   id              registry id (GLOBAL, a SCREEN_SHORTCUTS id, a POS_SHORTCUTS id)
 * @property {"global"|"screen"|"legacy"|"overlay"} kind
 * @property {ReadonlyArray<any>} [entries] bindings (defaults: GLOBAL_SHORTCUTS / SCREEN_SHORTCUTS[id].entries)
 * @property {() => Record<string, any>} [getHandlers]
 * @property {() => ReadonlyArray<string>} [getSuppress]  e.g. () => ["nav"]
 * @property {ReadonlyArray<string>} [legacyKeys] display keys a legacy screen owns
 * @property {string}   [title]
 * @property {string}   [suppressMessage]
 * @property {() => boolean} [isActive] e.g. « my dialog is the top one » (an
 *   overlay under a confirmation never fires)
 */

/**
 * @param {{ isModalOpen?: (e: any) => boolean, notify?: (message: string, tone?: string) => void, navSuppressedMessage?: string }} [deps]
 */
export function createShortcutRegistry(deps = {}) {
  const isModalOpen = deps.isModalOpen ?? (() => false);
  const notify = deps.notify ?? (() => {});
  const navSuppressedMessage = deps.navSuppressedMessage ?? "";
  /** @type {Array<any>} */
  let scopes = [];
  let seq = 0;
  const listeners = new Set();
  const emit = () => listeners.forEach((l) => l());

  function register(spec) {
    const kind = spec.kind ?? SCOPE_KINDS.SCREEN;
    const entries =
      spec.entries ??
      (kind === SCOPE_KINDS.GLOBAL
        ? GLOBAL_SHORTCUTS
        : kind === SCOPE_KINDS.LEGACY
          ? []
          : (SCREEN_SHORTCUTS[spec.id]?.entries ?? []));
    const scope = {
      token: (seq += 1),
      id: spec.id,
      kind,
      entries,
      getHandlers: spec.getHandlers ?? (() => ({})),
      getSuppress: spec.getSuppress ?? (() => []),
      isActive: spec.isActive ?? (() => true),
      legacyKeys: new Set(spec.legacyKeys ?? []),
      title: spec.title ?? SCREEN_SHORTCUTS[spec.id]?.title,
      suppressMessage: spec.suppressMessage,
    };
    scopes = [...scopes, scope];
    emit();
    return () => {
      scopes = scopes.filter((s) => s !== scope);
      emit();
    };
  }

  /** Scopes innermost first. */
  function ordered() {
    const rank = { overlay: 0, screen: 1, legacy: 1, global: 2 };
    return scopes.filter((s) => s.isActive()).sort((a, b) => rank[a.kind] - rank[b.kind] || b.token - a.token);
  }

  /**
   * Decide what a keydown does (no side effect).
   * @returns {null | {type: "run"|"swallow"|"suppressed"|"disabled"|"legacy", scope?: any, binding?: any, handler?: any, index?: number, message?: string}}
   */
  function resolve(e) {
    if (!e || e.isComposing || e.keyCode === 229) return null;
    if (isAltGraph(e)) return null;
    const target = e.target;
    const inText = isTextField(target);
    const inWidget = isInsideWidget(target);
    const modal = Boolean(isModalOpen(e));
    const list = ordered();
    const legacy = list.filter((s) => s.kind === SCOPE_KINDS.LEGACY);
    const suppressed = new Set(
      list
        .filter((s) => s.kind === SCOPE_KINDS.SCREEN || s.kind === SCOPE_KINDS.LEGACY)
        .flatMap((s) => s.getSuppress() ?? []),
    );
    for (const scope of list) {
      if (scope.kind === SCOPE_KINDS.LEGACY) continue;
      if (modal && scope.kind === SCOPE_KINDS.SCREEN) continue;
      for (const binding of scope.entries) {
        const index = binding.combos.findIndex((c) => matchCombo(e, parsed(c)));
        if (index < 0) continue;
        const combo = parsed(binding.combos[index]);
        const bare = !combo.ctrl && !combo.alt && !/^F\d+$/.test(combo.key) && combo.key !== "Escape";
        if (scope.kind === SCOPE_KINDS.SCREEN && inWidget && bare) continue;
        if (scope.kind === SCOPE_KINDS.GLOBAL) {
          if (legacy.some((l) => l.legacyKeys.has(binding.key))) return { type: "legacy", binding };
          if (modal && !MODAL_GLOBAL_IDS.includes(binding.id)) {
            if (binding.nav) return { type: "swallow", binding };
            continue;
          }
        }
        if (inText && !firesInTextField(combo)) continue;
        const handler = readHandler(scope.getHandlers()?.[binding.id]);
        if (!handler) continue; // declared but not bound here: an outer scope may bind it
        if (binding.commit && e.repeat) return { type: "swallow", binding };
        if (binding.nav && suppressed.has(SUPPRESS_GROUPS.NAV)) {
          const owner = list.find(
            (s) => (s.kind === SCOPE_KINDS.SCREEN || s.kind === SCOPE_KINDS.LEGACY) && (s.getSuppress() ?? []).includes(SUPPRESS_GROUPS.NAV),
          );
          return { type: "suppressed", binding, message: owner?.suppressMessage || navSuppressedMessage };
        }
        if (handler.disabled) return { type: "disabled", binding, message: handler.disabledReason };
        return { type: "run", scope, binding, handler, index };
      }
    }
    return null;
  }

  /** The capture-phase listener body. Returns the decision (tests). */
  function handleKeydown(e) {
    const d = resolve(e);
    if (!d) return null;
    switch (d.type) {
      case "run": {
        const out = d.handler.run(e, { index: d.index, binding: d.binding });
        if (out !== false) e.preventDefault?.();
        return { ...d, handled: out !== false };
      }
      case "swallow":
        e.preventDefault?.();
        return d;
      case "suppressed":
      case "disabled":
        e.preventDefault?.();
        if (d.message) notify(String(d.message), d.type === "suppressed" ? "warning" : "info");
        return d;
      default:
        return d; // legacy: the page's own listener handles it
    }
  }

  /**
   * What the F1 sheet shows: the innermost screen (or, under a modal, the
   * innermost overlay) with only the bindings that have a handler right now,
   * and the global bindings that are bound. The sheet documents what fires.
   */
  function snapshot(opts = {}) {
    const modal = Boolean(opts.modal);
    const list = ordered();
    const overlay = list.find((s) => s.kind === SCOPE_KINDS.OVERLAY);
    const screen = list.find((s) => s.kind === SCOPE_KINDS.SCREEN);
    const own = modal ? (overlay ?? null) : (screen ?? overlay ?? null);
    const bound = (scope) => {
      const h = scope.getHandlers() ?? {};
      return scope.entries.filter((b) => readHandler(h[b.id]));
    };
    const globals = [];
    const seen = new Set();
    for (const g of GLOBAL_SHORTCUTS) {
      const hit = list.some(
        (s) => s.kind === SCOPE_KINDS.GLOBAL && s.entries.includes(g) && readHandler(s.getHandlers()?.[g.id]),
      );
      if (hit && !seen.has(g.id)) {
        seen.add(g.id);
        globals.push(g);
      }
    }
    return {
      screenId: own?.id ?? null,
      title: own?.title ?? null,
      screen: own ? bound(own) : [],
      global: globals,
    };
  }

  return {
    register,
    resolve,
    handleKeydown,
    snapshot,
    subscribe(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    /** Test / debug view. */
    scopes: () => ordered().map((s) => ({ id: s.id, kind: s.kind })),
  };
}

/**
 * ActionBar actions generated from a screen's bindings (ux/01 §3.13): the
 * `bar` bindings that have a handler, in registry order, the primary verb
 * last; the key chip is the binding's display key and the click runs the SAME
 * handler the key runs.
 * @param {ReadonlyArray<any>} entries
 * @param {Record<string, any>} handlers
 * @param {Record<string, {disabled?: boolean, disabledReason?: any, loading?: boolean, label?: string, shortLabel?: string}>} [state]
 */
export function barActions(entries, handlers = {}, state = {}) {
  const out = [];
  for (const b of entries ?? []) {
    if (!b.bar) continue;
    const h = readHandler(handlers[b.id]);
    if (!h) continue;
    const s = state[b.id] ?? {};
    out.push({
      id: b.id,
      label: s.label ?? b.label,
      shortLabel: s.shortLabel ?? b.shortLabel,
      shortcut: b.key,
      icon: b.icon,
      primary: b.primary,
      danger: b.danger,
      disabled: Boolean(s.disabled ?? h.disabled),
      disabledReason: s.disabledReason ?? h.disabledReason,
      loading: s.loading,
      onClick: () => h.run(undefined, { index: 0, binding: b }),
    });
  }
  return [...out.filter((a) => !a.primary), ...out.filter((a) => a.primary)];
}
